/** @jsx jsx */
import { jsx, css } from "@emotion/react";
import { useMemo } from "react";
import { useWindowSize } from "react-use";
import SwiperCore, { Autoplay } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper.scss";
import { useTranslate } from "../../translate";
import { decorateRichText } from "./utils";

SwiperCore.use([Autoplay]);

const containerCSS = css`
    padding: 0 16px;
    position: relative;

    .trpr__primary-layout-itinerary-items[data-min-width~="450"] & {
        padding: 0 24px;
    }

    .trpr__primary-layout-itinerary-items[data-min-width~="650"] & {
        padding: 0 36px;
    }
`;
const cardCSS = css`
    background: #ffffff;
    box-shadow: 0px 4px 8px rgba(34, 32, 121, 0.2);
    border-radius: 8px;
    padding: 30px;

    .trpr__primary-layout-itinerary-items[data-min-width~="650"] & {
        padding: 36px;
    }
`;
const titleCSS = css`
    font-family: "Lexend Deca", sans-serif;
    text-transform: none;
    margin: 0 0 12px;
    color: #467c6f;
    font-size: 18px;
    line-height: 24px;
`;
const descriptionCSS = css`
    font-size: 15px;
    line-height: 24px;
    color: #505050;
    opacity: 0.75;
    margin-bottom: 16px;

    p {
        margin: 0;
    }
`;
const linkCSS = ({ isActive }) => css`
    background: #e1ebe9;
    border-radius: 4px;
    height: 34px;
    font-weight: 600;
    font-size: 15px;
    line-height: 24px;
    text-decoration: none !important;
    padding: 0 12px;
    display: inline-flex;
    align-items: center;
    border: 1px solid #467c6f;
    transition: all 300ms linear;

    &,
    &:hover {
        color: #467c6f;
    }

    ${isActive &&
        `
        background: #467c6f;

        &,
        &:hover {
            color: #fff;
        }
    `}
`;
const imgWidthOnWiderContainer = 323;
const imgCSS = css`
    width: 100%;
    height: 240px;
    border-radius: 8px;
    display: block;
    object-fit: cover;
    background: #d3d2fc;

    .trpr__primary-layout-itinerary-items[data-min-width~="450"] & {
        width: ${imgWidthOnWiderContainer}px;
        height: 200px;
    }
`;
const swiperCSS = css`
    pointer-events: none;
    margin-bottom: 24px;
    position: relative;

    &::after {
        content: "";
        background: linear-gradient(270deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
        height: 100%;
        width: 120px;
        position: absolute;
        z-index: 1;
        right: 0;
        top: 0;
        display: none;
    }

    .trpr__primary-layout-itinerary-items[data-min-width~="450"] & {
        margin-bottom: 36px;

        &::after {
            display: block;
        }
    }
`;

function ItinerarySuggestion(props) {
    const { isActive, item, className, ...attrs } = props;
    const relatedAtlas = item.relatedAtlas || item.en.relatedAtlas;
    const { width: windowWidth } = useWindowSize();
    const translate = useTranslate();
    const images = useMemo(() => {
        try {
            const stops = item.placeData.items.filter(item => item.type === "stop");
            return stops
                .map(stop => stop?.image?.thumbnail2x)
                .filter(Boolean)
                .slice(0, 3);
        } catch (e) {
            return [];
        }
    }, [item]);
    const placeLink = item.externalLink || relatedAtlas?.url;
    const description = translate(item, "description");
    const newDescription = useMemo(() => decorateRichText(description), [description]);

    let slideWidth;
    if (windowWidth >= 1001) {
        slideWidth = imgWidthOnWiderContainer;
    } else if (windowWidth >= 851) {
        slideWidth = undefined;
    } else if (windowWidth >= 450) {
        slideWidth = imgWidthOnWiderContainer;
    }

    const swiperEl = useMemo(() => {
        if (images.length === 0) {
            return null;
        }
        return (
            <Swiper
                mousewheel={false}
                grabCursor={true}
                simulateTouch={false}
                speed={200}
                spaceBetween={24}
                slidesPerView={1}
                autoplay={{
                    delay: 3000,
                    disableOnInteraction: false,
                }}
                breakpoints={{
                    450: { slidesPerView: "auto" },
                    851: { slidesPerView: 1 },
                    1001: { slidesPerView: "auto" },
                }}
            >
                {images.map((img, index) => (
                    <SwiperSlide style={{ width: slideWidth }} key={index}>
                        <img css={imgCSS} src={img} alt="" />
                    </SwiperSlide>
                ))}
            </Swiper>
        );
    }, [slideWidth, images]);

    return (
        <div css={containerCSS} className={className} {...attrs}>
            <div css={cardCSS}>
                {images.length > 0 && <div css={swiperCSS}>{swiperEl}</div>}
                <h3 css={titleCSS}>{translate(item, "title")}</h3>
                <div css={descriptionCSS} dangerouslySetInnerHTML={{ __html: newDescription }}></div>
                <a css={linkCSS({ isActive })} href={placeLink} target="_blank" rel="noopener noreferrer">
                    {translate(item, "buttonLabel")}
                </a>
            </div>
        </div>
    );
}

export default ItinerarySuggestion;
